/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        System Configuration & Dynamic Settings View (Task 11.5.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import {
  Settings,
  Mail,
  Bot,
  Coins,
  ShieldAlert,
  Save,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Server,
  Globe,
  Sparkles,
  Lock,
  Sliders,
  Send,
  Loader2,
  Share2,
  ExternalLink,
  Database,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { getAdminDomains } from '../lib/urls';
import { getAdminToken } from '../lib/api-client';
import { FeatureFlagsView } from './FeatureFlagsView';
import { DemoDataManagerTab } from './DemoDataManagerTab';
import type {
  SystemSettings,
  UpdateSystemSettingsInput,
  GeneralSettings,
  MailSettings,
  AiDefaultSettings,
  BillingSettings,
  SocialLoginSettings,
} from '@sofia/shared-types';

type TabKey = 'general' | 'mail' | 'ai' | 'billing' | 'social' | 'featureFlags' | 'demoData';

export const SystemSettingsView: React.FC = () => {
  const { t } = useI18n();
  const domains = getAdminDomains();

  const [activeTab, setActiveTab] = useState<TabKey>('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showResendKey, setShowResendKey] = useState(false);
  const [showSesSecretKey, setShowSesSecretKey] = useState(false);

  // Test email state
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailSuccess, setTestEmailSuccess] = useState<string | null>(null);
  const [testEmailError, setTestEmailError] = useState<string | null>(null);

  // Original state from backend
  const [originalSettings, setOriginalSettings] = useState<SystemSettings | null>(null);

  // Form draft state
  const [generalDraft, setGeneralDraft] = useState<GeneralSettings>({
    siteName: 'Sophia Platform',
    siteDescription: 'Next-Generation Edge-Native AI SaaS Platform powered by Cloudflare Workers',
    defaultLocale: 'vi',
    allowedLocales: ['vi', 'en', 'ja', 'ko', 'zh'],
    maintenanceMode: false,
    allowRegistrations: true,
  });

  const [mailDraft, setMailDraft] = useState<MailSettings>({
    driver: 'smtp',
    fromName: 'Sophia Platform',
    fromEmail: 'no-reply@sophiaplatform.com',
    smtpHost: 'smtp.sendgrid.net',
    smtpPort: 587,
    smtpUser: 'apikey',
    smtpPassword: '••••••••',
    smtpEncryption: 'tls',
    resendApiKey: '••••••••',
    awsSesRegion: 'us-east-1',
    awsSesAccessKeyId: '••••••••',
    awsSesSecretAccessKey: '••••••••',
  });

  const [aiDraft, setAiDraft] = useState<AiDefaultSettings>({
    defaultTextModel: 'mod_gpt_4o_mini',
    defaultImageModel: 'mod_flux_1_schnell',
    defaultVoiceModel: 'mod_tts_1',
    defaultTemperature: 0.7,
    defaultMaxTokens: 4096,
  });

  const [billingDraft, setBillingDraft] = useState<BillingSettings>({
    defaultSignupCredits: 100,
    currency: 'USD',
  });

  const [socialDraft, setSocialDraft] = useState<SocialLoginSettings>({
    google: { enabled: false, clientId: '', clientSecret: '' },
    github: { enabled: false, clientId: '', clientSecret: '' },
    apple: { enabled: false, clientId: '', teamId: '', keyId: '', privateKey: '' },
  });

  const [showGoogleSecret, setShowGoogleSecret] = useState(false);
  const [showGithubSecret, setShowGithubSecret] = useState(false);
  const [showApplePrivateKey, setShowApplePrivateKey] = useState(false);
  const [copiedCallback, setCopiedCallback] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setErrorMsg(null);

      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/settings`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const body = (await res.json()) as any;
      if (body.success && body.data) {
        const data: SystemSettings = body.data;
        setOriginalSettings(data);
        setGeneralDraft(data.general);
        setMailDraft(data.mail);
        setAiDraft(data.ai);
        setBillingDraft(data.billing);
        if (data.social) {
          setSocialDraft(data.social);
        }
      }
    } catch (err: any) {
      console.error('[SystemSettingsView] fetch error:', err);
      setErrorMsg(err.message || 'Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMsg(null);
      setSuccessMsg(null);

      const payload: UpdateSystemSettingsInput = {
        general: generalDraft,
        mail: mailDraft,
        ai: aiDraft,
        billing: billingDraft,
        social: socialDraft,
      };

      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/settings`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errJson = ((await res.json().catch(() => ({})))) as any;
        throw new Error(errJson.error?.message || `HTTP ${res.status}`);
      }

      const body = (await res.json()) as any;
      if (body.success && body.data) {
        setOriginalSettings(body.data);
        setGeneralDraft(body.data.general);
        setMailDraft(body.data.mail);
        setAiDraft(body.data.ai);
        setBillingDraft(body.data.billing);
        if (body.data.social) {
          setSocialDraft(body.data.social);
        }
        setSuccessMsg(t('admin.settings.actions.saveSuccess'));
        setTimeout(() => setSuccessMsg(null), 5000);
      }
    } catch (err: any) {
      console.error('[SystemSettingsView] save error:', err);
      setErrorMsg(t('admin.settings.actions.saveError', { error: err.message }));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (originalSettings) {
      setGeneralDraft(originalSettings.general);
      setMailDraft(originalSettings.mail);
      setAiDraft(originalSettings.ai);
      setBillingDraft(originalSettings.billing);
      if (originalSettings.social) {
        setSocialDraft(originalSettings.social);
      }
      setErrorMsg(null);
      setSuccessMsg(null);
      setTestEmailSuccess(null);
      setTestEmailError(null);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setSendingTestEmail(true);
      setTestEmailSuccess(null);
      setTestEmailError(null);

      const target = testEmailRecipient.trim() || mailDraft.fromEmail || 'admin@sophiaplatform.com';
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/settings/mail/test`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ toEmail: target }),
      });

      const body = (await res.json().catch(() => ({}))) as any;
      if (!res.ok || !body.success) {
        throw new Error(body.error?.message || `HTTP ${res.status}`);
      }

      setTestEmailSuccess(
        t('admin.settings.mail.testEmailSuccess', {
          driver: (body.data?.driver || mailDraft.driver).toUpperCase(),
          id: body.data?.messageId || 'OK',
        }),
      );
    } catch (err: any) {
      console.error('[SystemSettingsView] sendTestEmail error:', err);
      setTestEmailError(t('admin.settings.mail.testEmailError', { error: err.message || 'Dispatch failed' }));
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-muted-fg animate-pulse space-y-3">
        <Server className="w-8 h-8 animate-spin mx-auto text-primary" />
        <p className="text-sm">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2.5">
            <Settings className="w-6 h-6 text-primary" />
            {t('admin.settings.title')}
          </h1>
          <p className="text-xs text-muted-fg mt-1 max-w-2xl">
            {t('admin.settings.subtitle')}
          </p>
        </div>

        {/* Action Buttons */}
        {activeTab !== 'featureFlags' && activeTab !== 'demoData' && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDiscard}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-foreground bg-surface border border-surface-border rounded-xl hover:bg-surface-elevated transition-colors disabled:opacity-50"
            >
              <RotateCcw className="w-3.5 h-3.5 text-muted-fg" />
              {t('admin.settings.actions.discard')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-primary-fg bg-primary rounded-xl hover:bg-primary-hover shadow-sm transition-colors disabled:opacity-50 shadow-primary/20"
            >
              <Save className={`w-3.5 h-3.5 ${saving ? 'animate-spin' : ''}`} />
              {saving ? t('admin.settings.actions.saving') : t('admin.settings.actions.save')}
            </button>
          </div>
        )}
      </div>

      {/* Alert Banners */}
      {successMsg && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs flex items-center gap-2.5 shadow-sm">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-center gap-2.5 shadow-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex border-b border-surface-border gap-2 overflow-x-auto pb-px">
        <button
          type="button"
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2.5 text-xs font-medium rounded-t-xl transition-all border-b-2 flex items-center gap-2 ${
            activeTab === 'general'
              ? 'border-primary text-primary bg-primary/5 font-semibold'
              : 'border-transparent text-muted-fg hover:text-foreground hover:bg-surface-elevated/50'
          }`}
        >
          <Globe className="w-4 h-4" />
          {t('admin.settings.tabs.general')}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('mail')}
          className={`px-4 py-2.5 text-xs font-medium rounded-t-xl transition-all border-b-2 flex items-center gap-2 ${
            activeTab === 'mail'
              ? 'border-primary text-primary bg-primary/5 font-semibold'
              : 'border-transparent text-muted-fg hover:text-foreground hover:bg-surface-elevated/50'
          }`}
        >
          <Mail className="w-4 h-4" />
          {t('admin.settings.tabs.mail')}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('ai')}
          className={`px-4 py-2.5 text-xs font-medium rounded-t-xl transition-all border-b-2 flex items-center gap-2 ${
            activeTab === 'ai'
              ? 'border-primary text-primary bg-primary/5 font-semibold'
              : 'border-transparent text-muted-fg hover:text-foreground hover:bg-surface-elevated/50'
          }`}
        >
          <Bot className="w-4 h-4" />
          {t('admin.settings.tabs.ai')}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('billing')}
          className={`px-4 py-2.5 text-xs font-medium rounded-t-xl transition-all border-b-2 flex items-center gap-2 ${
            activeTab === 'billing'
              ? 'border-primary text-primary bg-primary/5 font-semibold'
              : 'border-transparent text-muted-fg hover:text-foreground hover:bg-surface-elevated/50'
          }`}
        >
          <Coins className="w-4 h-4" />
          {t('admin.settings.tabs.billing')}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('social')}
          className={`px-4 py-2.5 text-xs font-medium rounded-t-xl transition-all border-b-2 flex items-center gap-2 ${
            activeTab === 'social'
              ? 'border-primary text-primary bg-primary/5 font-semibold'
              : 'border-transparent text-muted-fg hover:text-foreground hover:bg-surface-elevated/50'
          }`}
        >
          <Share2 className="w-4 h-4" />
          {t('admin.settings.tabs.social')}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('featureFlags')}
          className={`px-4 py-2.5 text-xs font-medium rounded-t-xl transition-all border-b-2 flex items-center gap-2 ${
            activeTab === 'featureFlags'
              ? 'border-primary text-primary bg-primary/5 font-semibold'
              : 'border-transparent text-muted-fg hover:text-foreground hover:bg-surface-elevated/50'
          }`}
        >
          <Sliders className="w-4 h-4" />
          {t('admin.featureFlags.tabTitle')}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('demoData')}
          className={`px-4 py-2.5 text-xs font-medium rounded-t-xl transition-all border-b-2 flex items-center gap-2 ${
            activeTab === 'demoData'
              ? 'border-primary text-primary bg-primary/5 font-semibold'
              : 'border-transparent text-muted-fg hover:text-foreground hover:bg-surface-elevated/50'
          }`}
        >
          <Database className="w-4 h-4" />
          {t('admin.settings.tabs.demoData') || 'Demo Data Environment'}
        </button>
      </div>

      {/* Tab 1: General Settings */}
      {activeTab === 'general' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" />
              {t('admin.settings.general.siteName')} & Meta
            </h3>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                {t('admin.settings.general.siteName')}
              </label>
              <input
                type="text"
                value={generalDraft.siteName}
                onChange={(e) => setGeneralDraft({ ...generalDraft, siteName: e.target.value })}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                {t('admin.settings.general.siteDescription')}
              </label>
              <textarea
                rows={3}
                value={generalDraft.siteDescription}
                onChange={(e) => setGeneralDraft({ ...generalDraft, siteDescription: e.target.value })}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  {t('admin.settings.general.defaultLocale')}
                </label>
                <select
                  value={generalDraft.defaultLocale}
                  onChange={(e) => setGeneralDraft({ ...generalDraft, defaultLocale: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="vi">Tiếng Việt (vi)</option>
                  <option value="en">English (en)</option>
                  <option value="ja">日本語 (ja)</option>
                  <option value="ko">한국어 (ko)</option>
                  <option value="zh">中文 (zh)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  {t('admin.settings.general.allowedLocales')}
                </label>
                <input
                  type="text"
                  value={generalDraft.allowedLocales.join(', ')}
                  onChange={(e) =>
                    setGeneralDraft({
                      ...generalDraft,
                      allowedLocales: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="vi, en, ja..."
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-5">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-500" />
              {t('admin.settings.general.maintenanceMode')} & Access Control
            </h3>

            {/* Maintenance Mode Toggle */}
            <div className="p-4 rounded-xl border border-surface-border bg-surface-elevated/50 flex items-start justify-between gap-4">
              <div className="space-y-1">
                <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  {t('admin.settings.general.maintenanceMode')}
                  {generalDraft.maintenanceMode && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500 text-amber-950">
                      ACTIVE
                    </span>
                  )}
                </span>
                <p className="text-[11px] text-muted-fg leading-relaxed">
                  {t('admin.settings.general.maintenanceHint')}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                <input
                  type="checkbox"
                  checked={generalDraft.maintenanceMode}
                  onChange={(e) => setGeneralDraft({ ...generalDraft, maintenanceMode: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-surface-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>

            {/* Registration Toggle */}
            <div className="p-4 rounded-xl border border-surface-border bg-surface-elevated/50 flex items-start justify-between gap-4">
              <div className="space-y-1">
                <span className="text-xs font-semibold text-foreground">
                  {t('admin.settings.general.allowRegistrations')}
                </span>
                <p className="text-[11px] text-muted-fg leading-relaxed">
                  {t('admin.settings.general.allowRegistrationsHint')}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                <input
                  type="checkbox"
                  checked={generalDraft.allowRegistrations}
                  onChange={(e) => setGeneralDraft({ ...generalDraft, allowRegistrations: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-surface-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Mail & SMTP / SES Settings */}
      {activeTab === 'mail' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              {t('admin.settings.mail.driver')} & Identity
            </h3>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                {t('admin.settings.mail.driver')}
              </label>
              <select
                value={mailDraft.driver}
                onChange={(e) => setMailDraft({ ...mailDraft, driver: e.target.value as any })}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-medium"
              >
                <option value="smtp">Standard Outbound SMTP (TLS/SSL)</option>
                <option value="aws_ses">Amazon SES (Simple Email Service v2 SigV4)</option>
                <option value="resend">Resend API Edge Mailer</option>
                <option value="mock">Local Development Mock (Console Logging)</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  {t('admin.settings.mail.fromName')}
                </label>
                <input
                  type="text"
                  value={mailDraft.fromName}
                  onChange={(e) => setMailDraft({ ...mailDraft, fromName: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  {t('admin.settings.mail.fromEmail')}
                </label>
                <input
                  type="email"
                  value={mailDraft.fromEmail}
                  onChange={(e) => setMailDraft({ ...mailDraft, fromEmail: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>

            {mailDraft.driver === 'resend' && (
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  {t('admin.settings.mail.resendApiKey')}
                </label>
                <div className="relative">
                  <input
                    type={showResendKey ? 'text' : 'password'}
                    value={mailDraft.resendApiKey || ''}
                    onChange={(e) => setMailDraft({ ...mailDraft, resendApiKey: e.target.value })}
                    placeholder={t('admin.settings.mail.resendApiKeyPlaceholder')}
                    className="w-full px-3 py-2 pr-10 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResendKey(!showResendKey)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-fg hover:text-foreground"
                  >
                    {showResendKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Driver-Specific Credentials */}
          {mailDraft.driver === 'aws_ses' && (
            <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Server className="w-4 h-4 text-primary" />
                Amazon SES v2 SigV4 Configuration
              </h3>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  {t('admin.settings.mail.awsSesRegion')}
                </label>
                <select
                  value={mailDraft.awsSesRegion || 'us-east-1'}
                  onChange={(e) => setMailDraft({ ...mailDraft, awsSesRegion: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                >
                  <option value="us-east-1">us-east-1 (US East - N. Virginia)</option>
                  <option value="us-east-2">us-east-2 (US East - Ohio)</option>
                  <option value="us-west-2">us-west-2 (US West - Oregon)</option>
                  <option value="eu-west-1">eu-west-1 (Europe - Ireland)</option>
                  <option value="eu-central-1">eu-central-1 (Europe - Frankfurt)</option>
                  <option value="ap-southeast-1">ap-southeast-1 (Asia Pacific - Singapore)</option>
                  <option value="ap-southeast-2">ap-southeast-2 (Asia Pacific - Sydney)</option>
                  <option value="ap-northeast-1">ap-northeast-1 (Asia Pacific - Tokyo)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  {t('admin.settings.mail.awsSesAccessKeyId')}
                </label>
                <input
                  type="text"
                  value={mailDraft.awsSesAccessKeyId || ''}
                  onChange={(e) => setMailDraft({ ...mailDraft, awsSesAccessKeyId: e.target.value })}
                  placeholder={t('admin.settings.mail.awsSesAccessKeyIdPlaceholder')}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  {t('admin.settings.mail.awsSesSecretAccessKey')}
                </label>
                <div className="relative">
                  <input
                    type={showSesSecretKey ? 'text' : 'password'}
                    value={mailDraft.awsSesSecretAccessKey || ''}
                    onChange={(e) => setMailDraft({ ...mailDraft, awsSesSecretAccessKey: e.target.value })}
                    placeholder={t('admin.settings.mail.awsSesSecretAccessKeyPlaceholder')}
                    className="w-full px-3 py-2 pr-10 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSesSecretKey(!showSesSecretKey)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-fg hover:text-foreground"
                  >
                    {showSesSecretKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <p className="text-[10px] text-muted-fg mt-1">
                  {t('admin.settings.mail.awsSesSecretAccessKeyPlaceholder')}
                </p>
              </div>
            </div>
          )}

          {mailDraft.driver === 'smtp' && (
            <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Server className="w-4 h-4 text-primary" />
                SMTP Server Configuration
              </h3>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {t('admin.settings.mail.smtpHost')}
                  </label>
                  <input
                    type="text"
                    value={mailDraft.smtpHost}
                    onChange={(e) => setMailDraft({ ...mailDraft, smtpHost: e.target.value })}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {t('admin.settings.mail.smtpPort')}
                  </label>
                  <input
                    type="number"
                    value={mailDraft.smtpPort}
                    onChange={(e) => setMailDraft({ ...mailDraft, smtpPort: Number(e.target.value) })}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {t('admin.settings.mail.smtpUser')}
                  </label>
                  <input
                    type="text"
                    value={mailDraft.smtpUser}
                    onChange={(e) => setMailDraft({ ...mailDraft, smtpUser: e.target.value })}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {t('admin.settings.mail.smtpEncryption')}
                  </label>
                  <select
                    value={mailDraft.smtpEncryption}
                    onChange={(e) => setMailDraft({ ...mailDraft, smtpEncryption: e.target.value as any })}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="tls">TLS (STARTTLS)</option>
                    <option value="ssl">SSL (Implicit)</option>
                    <option value="none">Plain Text (None)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  {t('admin.settings.mail.smtpPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={mailDraft.smtpPassword || ''}
                    onChange={(e) => setMailDraft({ ...mailDraft, smtpPassword: e.target.value })}
                    placeholder={t('admin.settings.mail.smtpPasswordPlaceholder')}
                    className="w-full px-3 py-2 pr-10 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-fg hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <p className="text-[10px] text-muted-fg mt-1">
                  {t('admin.settings.mail.smtpPasswordPlaceholder')}
                </p>
              </div>
            </div>
          )}

          {mailDraft.driver === 'mock' && (
            <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-3 flex flex-col justify-center">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Server className="w-4 h-4 text-primary" />
                Local Development Mock Driver
              </h3>
              <p className="text-xs text-muted-fg leading-relaxed">
                When using the <span className="font-semibold text-foreground">Mock Driver</span>, outbound emails will not trigger external network calls.
                Messages are simulated and output directly to the Cloudflare Worker console and test logs.
              </p>
            </div>
          )}

          {/* Mail Diagnostics & Send Test Email Card */}
          <div className="col-span-1 md:col-span-2 p-5 rounded-2xl bg-surface border border-surface-border space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Send className="w-4 h-4 text-primary" />
                  {t('admin.settings.mail.testEmailCardTitle')}
                </h3>
                <p className="text-xs text-muted-fg mt-0.5">
                  {t('admin.settings.mail.testEmailCardDesc')}
                </p>
              </div>
              <span className="text-[11px] px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium w-fit">
                Active Transport: {mailDraft.driver.toUpperCase()}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="flex-1 w-full">
                <input
                  type="email"
                  value={testEmailRecipient}
                  onChange={(e) => setTestEmailRecipient(e.target.value)}
                  placeholder={mailDraft.fromEmail || t('admin.settings.mail.testEmailRecipientPlaceholder')}
                  className="w-full px-3.5 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <button
                type="button"
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail}
                className="w-full sm:w-auto px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-sm shrink-0"
              >
                {sendingTestEmail ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {t('admin.settings.mail.sendingTestEmail')}
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    {t('admin.settings.mail.sendTestEmail')}
                  </>
                )}
              </button>
            </div>

            {testEmailSuccess && (
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>{testEmailSuccess}</span>
              </div>
            )}

            {testEmailError && (
              <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{testEmailError}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab 3: AI Inference Defaults */}
      {activeTab === 'ai' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Bot className="w-4 h-4 text-primary" />
              Primary Models Selection
            </h3>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                {t('admin.settings.ai.defaultTextModel')}
              </label>
              <select
                value={aiDraft.defaultTextModel}
                onChange={(e) => setAiDraft({ ...aiDraft, defaultTextModel: e.target.value })}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
              >
                <option value="mod_gpt_4o_mini">mod_gpt_4o_mini (GPT-4o Mini - Ultra Fast & Cost Efficient)</option>
                <option value="mod_gpt_4o">mod_gpt_4o (GPT-4o Omnimodal Flagship)</option>
                <option value="mod_claude_35_sonnet">mod_claude_35_sonnet (Claude 3.5 Sonnet)</option>
                <option value="mod_claude_35_haiku">mod_claude_35_haiku (Claude 3.5 Haiku)</option>
                <option value="mod_gemini_15_flash">mod_gemini_15_flash (Gemini 1.5 Flash)</option>
                <option value="mod_deepseek_v3">mod_deepseek_v3 (DeepSeek V3 Sovereign)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                {t('admin.settings.ai.defaultImageModel')}
              </label>
              <select
                value={aiDraft.defaultImageModel}
                onChange={(e) => setAiDraft({ ...aiDraft, defaultImageModel: e.target.value })}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
              >
                <option value="mod_flux_1_schnell">mod_flux_1_schnell (FLUX.1 Schnell - 4-step real-time)</option>
                <option value="mod_flux_1_dev">mod_flux_1_dev (FLUX.1 Dev High-Res)</option>
                <option value="mod_dall_e_3">mod_dall_e_3 (OpenAI DALL-E 3 HD)</option>
                <option value="mod_sd_35_turbo">mod_sd_35_turbo (Stable Diffusion 3.5 Turbo)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                {t('admin.settings.ai.defaultVoiceModel')}
              </label>
              <select
                value={aiDraft.defaultVoiceModel}
                onChange={(e) => setAiDraft({ ...aiDraft, defaultVoiceModel: e.target.value })}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
              >
                <option value="mod_tts_1">mod_tts_1 (OpenAI TTS-1 Standard)</option>
                <option value="mod_tts_1_hd">mod_tts_1_hd (OpenAI TTS-1 High Definition)</option>
                <option value="mod_eleven_multilingual_v2">mod_eleven_multilingual_v2 (ElevenLabs Turbo v2)</option>
                <option value="mod_google_neural2">mod_google_neural2 (Google Cloud Neural2 / Journey)</option>
              </select>
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-5">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Inference Sampling Defaults
            </h3>

            {/* Temperature Slider */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <label className="font-medium text-foreground">
                  {t('admin.settings.ai.defaultTemperature')}
                </label>
                <span className="font-mono font-semibold text-primary">{aiDraft.defaultTemperature}</span>
              </div>
              <input
                type="range"
                min="0"
                max="2"
                step="0.05"
                value={aiDraft.defaultTemperature}
                onChange={(e) => setAiDraft({ ...aiDraft, defaultTemperature: parseFloat(e.target.value) })}
                className="w-full accent-primary"
              />
              <p className="text-[11px] text-muted-fg mt-1">
                {t('admin.settings.ai.defaultTemperatureHint')}
              </p>
            </div>

            {/* Max Output Tokens */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                {t('admin.settings.ai.defaultMaxTokens')}
              </label>
              <input
                type="number"
                min="100"
                max="32000"
                step="128"
                value={aiDraft.defaultMaxTokens}
                onChange={(e) => setAiDraft({ ...aiDraft, defaultMaxTokens: parseInt(e.target.value, 10) })}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
              />
            </div>
          </div>
        </div>
      )}

      {/* Tab 4: Billing & Credit Settings */}
      {activeTab === 'billing' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Coins className="w-4 h-4 text-emerald-500" />
              {t('admin.settings.tabs.billing')} Economy
            </h3>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                {t('admin.settings.billing.defaultSignupCredits')}
              </label>
              <input
                type="number"
                min="0"
                step="50"
                value={billingDraft.defaultSignupCredits}
                onChange={(e) =>
                  setBillingDraft({ ...billingDraft, defaultSignupCredits: Math.max(0, parseInt(e.target.value, 10)) })
                }
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono font-semibold"
              />
              <p className="text-[11px] text-muted-fg mt-1">
                {t('admin.settings.billing.defaultSignupCreditsHint')}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                {t('admin.settings.billing.currency')}
              </label>
              <select
                value={billingDraft.currency}
                onChange={(e) => setBillingDraft({ ...billingDraft, currency: e.target.value })}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-semibold"
              >
                <option value="USD">USD ($) - United States Dollar</option>
                <option value="VND">VND (₫) - Vietnam Dong</option>
                <option value="EUR">EUR (€) - Euro</option>
                <option value="GBP">GBP (£) - British Pound</option>
                <option value="JPY">JPY (¥) - Japanese Yen</option>
              </select>
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-3">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              Cloudflare Edge Architecture
            </h3>
            <p className="text-xs text-muted-fg leading-relaxed">
              When system configuration is saved, public flags like <code className="text-primary font-mono">maintenance_mode</code> and{' '}
              <code className="text-primary font-mono">allow_registrations</code> are instantly synchronized into Cloudflare KV{' '}
              (<code className="text-primary font-mono">SYSTEM_KEYS_KV</code>) with sub-millisecond edge resolution across 300+ global data centers.
            </p>
            {originalSettings?.updatedAt && (
              <div className="p-3 rounded-xl bg-surface-elevated text-[11px] text-muted-fg flex items-center justify-between">
                <span>Last updated at:</span>
                <span className="font-mono text-foreground">{new Date(originalSettings.updatedAt).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab: Social Logins (Google, GitHub, Apple) */}
      {activeTab === 'social' && (
        <div className="space-y-6">
          {/* Informational Header Card */}
          <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-2">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Share2 className="w-4 h-4 text-primary" />
              {t('admin.settings.social.title')}
            </h3>
            <p className="text-xs text-muted-fg leading-relaxed">
              {t('admin.settings.social.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Google OAuth Card */}
            <div className="p-6 rounded-2xl bg-surface border border-surface-border flex flex-col justify-between space-y-5 shadow-sm">
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-surface-elevated border border-surface-border flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {t('admin.settings.social.google.title')}
                      </h4>
                      <p className="text-[11px] text-muted-fg leading-tight mt-0.5">
                        {t('admin.settings.social.google.desc')}
                      </p>
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={socialDraft.google.enabled}
                      onChange={(e) =>
                        setSocialDraft({
                          ...socialDraft,
                          google: { ...socialDraft.google, enabled: e.target.checked },
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-surface-elevated peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary border border-surface-border"></div>
                  </label>
                </div>

                <div className="space-y-3 pt-2">
                  <div>
                    <label className="block text-[11px] font-medium text-foreground mb-1">
                      {t('admin.settings.social.google.clientId')}
                    </label>
                    <input
                      type="text"
                      value={socialDraft.google.clientId}
                      onChange={(e) =>
                        setSocialDraft({
                          ...socialDraft,
                          google: { ...socialDraft.google, clientId: e.target.value.trim() },
                        })
                      }
                      placeholder={t('admin.settings.social.google.clientIdPlaceholder')}
                      className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg/60 focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-foreground mb-1">
                      {t('admin.settings.social.google.clientSecret')}
                    </label>
                    <div className="relative">
                      <input
                        type={showGoogleSecret ? 'text' : 'password'}
                        value={socialDraft.google.clientSecret || ''}
                        onChange={(e) =>
                          setSocialDraft({
                            ...socialDraft,
                            google: { ...socialDraft.google, clientSecret: e.target.value },
                          })
                        }
                        placeholder={t('admin.settings.social.google.clientSecretPlaceholder')}
                        className="w-full pl-3 pr-9 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg/60 focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowGoogleSecret(!showGoogleSecret)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-fg hover:text-foreground p-1"
                      >
                        {showGoogleSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  {/* Callback URL helper */}
                  <div className="p-3 rounded-xl bg-surface-elevated border border-surface-border space-y-1.5">
                    <span className="text-[10px] text-muted-fg block font-medium">
                      {t('admin.settings.social.callbackUrlNote')}
                    </span>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-mono text-foreground truncate select-all">
                        {`${domains.apiOrigin}/api/v1/auth/oauth/google/callback`}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${domains.apiOrigin}/api/v1/auth/oauth/google/callback`);
                          setCopiedCallback('google');
                          setTimeout(() => setCopiedCallback(null), 2000);
                        }}
                        className="px-2 py-1 rounded text-[10px] font-semibold bg-surface border border-surface-border text-foreground hover:bg-muted/40 transition-colors shrink-0"
                      >
                        {copiedCallback === 'google' ? t('admin.settings.social.copied') : t('admin.settings.social.copyUrl')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <a
                href="https://console.cloud.google.com/apis/credentials"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline pt-2 border-t border-surface-border"
              >
                <span>{t('admin.settings.social.google.docLink')}</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            {/* GitHub OAuth Card */}
            <div className="p-6 rounded-2xl bg-surface border border-surface-border flex flex-col justify-between space-y-5 shadow-sm">
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-surface-elevated border border-surface-border flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5 fill-current text-foreground" viewBox="0 0 24 24">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {t('admin.settings.social.github.title')}
                      </h4>
                      <p className="text-[11px] text-muted-fg leading-tight mt-0.5">
                        {t('admin.settings.social.github.desc')}
                      </p>
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={socialDraft.github.enabled}
                      onChange={(e) =>
                        setSocialDraft({
                          ...socialDraft,
                          github: { ...socialDraft.github, enabled: e.target.checked },
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-surface-elevated peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary border border-surface-border"></div>
                  </label>
                </div>

                <div className="space-y-3 pt-2">
                  <div>
                    <label className="block text-[11px] font-medium text-foreground mb-1">
                      {t('admin.settings.social.github.clientId')}
                    </label>
                    <input
                      type="text"
                      value={socialDraft.github.clientId}
                      onChange={(e) =>
                        setSocialDraft({
                          ...socialDraft,
                          github: { ...socialDraft.github, clientId: e.target.value.trim() },
                        })
                      }
                      placeholder={t('admin.settings.social.github.clientIdPlaceholder')}
                      className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg/60 focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-foreground mb-1">
                      {t('admin.settings.social.github.clientSecret')}
                    </label>
                    <div className="relative">
                      <input
                        type={showGithubSecret ? 'text' : 'password'}
                        value={socialDraft.github.clientSecret || ''}
                        onChange={(e) =>
                          setSocialDraft({
                            ...socialDraft,
                            github: { ...socialDraft.github, clientSecret: e.target.value },
                          })
                        }
                        placeholder={t('admin.settings.social.github.clientSecretPlaceholder')}
                        className="w-full pl-3 pr-9 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg/60 focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowGithubSecret(!showGithubSecret)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-fg hover:text-foreground p-1"
                      >
                        {showGithubSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  {/* Callback URL helper */}
                  <div className="p-3 rounded-xl bg-surface-elevated border border-surface-border space-y-1.5">
                    <span className="text-[10px] text-muted-fg block font-medium">
                      {t('admin.settings.social.callbackUrlNote')}
                    </span>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-mono text-foreground truncate select-all">
                        {`${domains.apiOrigin}/api/v1/auth/oauth/github/callback`}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${domains.apiOrigin}/api/v1/auth/oauth/github/callback`);
                          setCopiedCallback('github');
                          setTimeout(() => setCopiedCallback(null), 2000);
                        }}
                        className="px-2 py-1 rounded text-[10px] font-semibold bg-surface border border-surface-border text-foreground hover:bg-muted/40 transition-colors shrink-0"
                      >
                        {copiedCallback === 'github' ? t('admin.settings.social.copied') : t('admin.settings.social.copyUrl')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <a
                href="https://github.com/settings/developers"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline pt-2 border-t border-surface-border"
              >
                <span>{t('admin.settings.social.github.docLink')}</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            {/* Apple OpenID Connect Card */}
            <div className="p-6 rounded-2xl bg-surface border border-surface-border flex flex-col justify-between space-y-5 shadow-sm">
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-surface-elevated border border-surface-border flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5 fill-current text-foreground" viewBox="0 0 24 24">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.77 1.04-1.85.93-2.92-.9.04-1.99.6-2.63 1.37-.56.65-.95 1.75-.82 2.8 1 .08 2-.51 2.52-1.25z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {t('admin.settings.social.apple.title')}
                      </h4>
                      <p className="text-[11px] text-muted-fg leading-tight mt-0.5">
                        {t('admin.settings.social.apple.desc')}
                      </p>
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={socialDraft.apple.enabled}
                      onChange={(e) =>
                        setSocialDraft({
                          ...socialDraft,
                          apple: { ...socialDraft.apple, enabled: e.target.checked },
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-surface-elevated peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary border border-surface-border"></div>
                  </label>
                </div>

                <div className="space-y-3 pt-2">
                  <div>
                    <label className="block text-[11px] font-medium text-foreground mb-1">
                      {t('admin.settings.social.apple.clientId')}
                    </label>
                    <input
                      type="text"
                      value={socialDraft.apple.clientId}
                      onChange={(e) =>
                        setSocialDraft({
                          ...socialDraft,
                          apple: { ...socialDraft.apple, clientId: e.target.value.trim() },
                        })
                      }
                      placeholder={t('admin.settings.social.apple.clientIdPlaceholder')}
                      className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg/60 focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-medium text-foreground mb-1">
                        {t('admin.settings.social.apple.teamId')}
                      </label>
                      <input
                        type="text"
                        value={socialDraft.apple.teamId}
                        onChange={(e) =>
                          setSocialDraft({
                            ...socialDraft,
                            apple: { ...socialDraft.apple, teamId: e.target.value.trim() },
                          })
                        }
                        placeholder={t('admin.settings.social.apple.teamIdPlaceholder')}
                        className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg/60 focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-foreground mb-1">
                        {t('admin.settings.social.apple.keyId')}
                      </label>
                      <input
                        type="text"
                        value={socialDraft.apple.keyId}
                        onChange={(e) =>
                          setSocialDraft({
                            ...socialDraft,
                            apple: { ...socialDraft.apple, keyId: e.target.value.trim() },
                          })
                        }
                        placeholder={t('admin.settings.social.apple.keyIdPlaceholder')}
                        className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg/60 focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-foreground mb-1">
                      {t('admin.settings.social.apple.privateKey')}
                    </label>
                    <div className="relative">
                      <input
                        type={showApplePrivateKey ? 'text' : 'password'}
                        value={socialDraft.apple.privateKey || ''}
                        onChange={(e) =>
                          setSocialDraft({
                            ...socialDraft,
                            apple: { ...socialDraft.apple, privateKey: e.target.value },
                          })
                        }
                        placeholder={t('admin.settings.social.apple.privateKeyPlaceholder')}
                        className="w-full pl-3 pr-9 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg/60 focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApplePrivateKey(!showApplePrivateKey)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-fg hover:text-foreground p-1"
                      >
                        {showApplePrivateKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  {/* Callback URL helper */}
                  <div className="p-3 rounded-xl bg-surface-elevated border border-surface-border space-y-1.5">
                    <span className="text-[10px] text-muted-fg block font-medium">
                      {t('admin.settings.social.callbackUrlNote')}
                    </span>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-mono text-foreground truncate select-all">
                        {`${domains.apiOrigin}/api/v1/auth/oauth/apple/callback`}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${domains.apiOrigin}/api/v1/auth/oauth/apple/callback`);
                          setCopiedCallback('apple');
                          setTimeout(() => setCopiedCallback(null), 2000);
                        }}
                        className="px-2 py-1 rounded text-[10px] font-semibold bg-surface border border-surface-border text-foreground hover:bg-muted/40 transition-colors shrink-0"
                      >
                        {copiedCallback === 'apple' ? t('admin.settings.social.copied') : t('admin.settings.social.copyUrl')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <a
                href="https://developer.apple.com/account/resources/identifiers/list/serviceId"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline pt-2 border-t border-surface-border"
              >
                <span>{t('admin.settings.social.apple.docLink')}</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Tab 5: Feature Flags & Maintenance Mode */}
      {activeTab === 'featureFlags' && <FeatureFlagsView embedded />}

      {/* Tab 6: Demo Data Management & Seeding */}
      {activeTab === 'demoData' && <DemoDataManagerTab />}
    </div>
  );
};
